import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { apiClient } from '@/api/client';
import { API_FAMILY_MEMBERS } from '@/api/endpoints';
import { STUDENT_JOIN_OFFERS_QUERY_KEY } from '@/api/queryKeys';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icons';
import { Modal } from '@/components/Modal';
import { ROUTE_HOME } from '@/router/routes';
import { ButtonVariant, IconType, ModalSize, Size } from '@/types/components';
import type {
  AddFamilyMemberRequest,
  AddFamilyMemberResponse,
  StudentJoinOfferResponse,
} from '@/types/user';
import { unwrapAxiosResponse } from '@/utils/axios';
import { ErrorMessage, logRequestError } from '@/utils/errors';
import { submitOnEnter } from '@/utils/form';
import { filterPersonalIdInput, isValidPersonalId } from '@/utils/validation';

interface Props {
  isOpen: boolean;
  onSuccess: () => void;
  onNotFound: () => void;
  onClose: () => void;
}

export const AddStudentOfferModal: React.FC<Props> = ({
  isOpen,
  onSuccess,
  onNotFound,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [personalId, setPersonalId] = useState('');

  const { mutateAsync: addStudent, isPending } = useMutation({
    mutationFn: (payload: AddFamilyMemberRequest) =>
      apiClient
        .post<AddFamilyMemberResponse>(API_FAMILY_MEMBERS, payload)
        .then(unwrapAxiosResponse),
  });

  const isInvalidFormat = !isValidPersonalId(personalId);
  const hasFormatError = personalId.length >= 11 && isInvalidFormat;

  const resetState = () => {
    setPersonalId('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handlePersonalIdChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPersonalId(filterPersonalIdInput(event.target.value));
  };

  const handleSubmit = async () => {
    if (isPending || isInvalidFormat) {
      return;
    }

    try {
      const result = await addStudent({ personalIdentityNumber: personalId });

      if (result.joinedStudents.length === 0) {
        resetState();
        onNotFound();
        onClose();
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: STUDENT_JOIN_OFFERS_QUERY_KEY,
      });

      const remainingOffers = queryClient.getQueryData<
        StudentJoinOfferResponse[]
      >(STUDENT_JOIN_OFFERS_QUERY_KEY);

      resetState();
      onSuccess();
      onClose();

      if (!remainingOffers || remainingOffers.length === 0) {
        navigate(ROUTE_HOME, { replace: true });
      }
    } catch (error) {
      logRequestError(ErrorMessage.AddFamilyMember, error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      label={t('offers.add.title')}
      size={ModalSize.Small}
      onClose={handleClose}
    >
      <div className="flex shrink-0 flex-col gap-3 px-4 pt-[calc(24px+env(safe-area-inset-top))] pb-3 md:gap-2 md:px-5 md:pt-5 md:pb-4">
        <button
          type="button"
          onClick={handleClose}
          aria-label={t('global.back')}
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:hidden"
        >
          <Icon icon={IconType.ArrowLeft} />
        </button>
        <h2 className="text-title-36 text-foreground md:text-headline-20">
          {t('offers.add.title')}
        </h2>
      </div>

      <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-4 pb-4 md:px-5">
        <label className="text-body-16 text-foreground">
          {t('offers.add.inputLabel')}
        </label>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={12}
          value={personalId}
          onChange={handlePersonalIdChange}
          onKeyDown={submitOnEnter(handleSubmit)}
          placeholder={t('offers.add.inputPlaceholder')}
          className="h-14 rounded-xl md:h-12 border border-transparent bg-on-primary-surface px-4 text-body-16 text-foreground placeholder:text-subtle-foreground outline-none"
        />
        {hasFormatError && (
          <p className="text-body-14 text-destructive">
            {t('offers.add.formatError')}
          </p>
        )}
      </div>

      <div className="flex shrink-0 gap-3 border-t border-border bg-background px-4 pt-4 pb-[calc(24px+env(safe-area-inset-bottom))] md:border-t-0 md:p-5">
        <Button
          variant={ButtonVariant.Secondary}
          size={Size.Large}
          onClick={handleClose}
          className="flex-1"
        >
          {t('global.return')}
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          size={Size.Large}
          onClick={handleSubmit}
          disabled={isInvalidFormat}
          isLoading={isPending}
          className="flex-1"
        >
          {t('global.confirm')}
        </Button>
      </div>
    </Modal>
  );
};
